import { User } from 'lucide-react'
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/cn'
import { boxPercent, containRect, type DrawnRect, sameRect } from './highlight'

export interface FaceHighlightProps {
  /**
   * The rendered image: a blob URL the parent already resolved, because the
   * bytes sit behind the auth-gated files endpoint.
   */
  imageUrl: string | null
  /** Alt text, already translated. */
  alt: string
  /** `[x1, y1, x2, y2]` in 0–1 of this image (slice 01 orientation). */
  box: readonly number[] | null | undefined
  /** Translated label of the box itself, for assistive tech. */
  boxLabel: string
  className?: string
  /**
   * Further boxes to draw over the same image, positioned in percentages of it
   * — the layer they land in is the painted image, not this element.
   */
  children?: ReactNode
}

/**
 * Where the image is painted inside `image`, in the coordinates of the element
 * the highlight is positioned against. Re-measures on load and on every resize
 * of the image or its slot, so the boxes follow the photo at any size.
 */
function useDrawnRect(image: RefObject<HTMLImageElement | null>, imageUrl: string | null) {
  const [drawn, setDrawn] = useState<DrawnRect | null>(null)
  const measure = useCallback(() => {
    const element = image.current
    const rect =
      element &&
      containRect(
        { width: element.naturalWidth, height: element.naturalHeight },
        { width: element.clientWidth, height: element.clientHeight }
      )
    const next =
      rect && element
        ? { ...rect, left: rect.left + element.offsetLeft, top: rect.top + element.offsetTop }
        : null
    setDrawn(previous => (sameRect(previous, next) ? previous : next))
  }, [image])
  useEffect(() => {
    measure()
    const element = image.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    if (element.offsetParent instanceof Element) observer.observe(element.offsetParent)
    return () => observer.disconnect()
  }, [image, imageUrl, measure])
  return { drawn, measure }
}

/**
 * One photo with the face boxed. The box is positioned in percentages of the
 * *painted* image, so it follows the photo at any displayed size — thumbnail,
 * modal or full width — and stays on the face even when the image is
 * letterboxed inside its slot. Pure: no fetching, tokens only.
 */
export function FaceHighlight({
  imageUrl,
  alt,
  box,
  boxLabel,
  className,
  children,
}: FaceHighlightProps) {
  const rect = boxPercent(box, { pad: 0.08 })
  const image = useRef<HTMLImageElement>(null)
  const { drawn, measure } = useDrawnRect(image, imageUrl)
  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden rounded-md border border-border bg-muted',
        className
      )}
    >
      {imageUrl ? (
        <img
          ref={image}
          src={imageUrl}
          alt={alt}
          onLoad={measure}
          className="block max-h-full w-full object-contain"
        />
      ) : (
        <User className="size-16 text-muted-foreground" aria-hidden />
      )}
      {imageUrl && (rect || children) ? (
        <div
          className="pointer-events-none absolute"
          data-testid="face-highlight-layer"
          style={drawn ? { ...drawn } : { inset: 0 }}
        >
          {rect ? (
            <div
              role="img"
              aria-label={boxLabel}
              data-testid="face-highlight-box"
              className="absolute rounded-sm border-2 border-info ring-1 ring-background/60"
              style={{ ...rect }}
            />
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  )
}
